import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import { Link, useHistory, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import PageLayout from '../common/PageLayout'
import PageHeading from '../common/PageHeading'
import Divider from '../../../../components/atoms/Divider'
import Loading from '../../../../components/molecules/Loading'
import SydLabel from '../../../../components/commonDesign/SydLabel'
import SydInput from '../../../../components/commonDesign/SydInput'
import SydSelect from '../../../../components/commonDesign/SydSelect'
import SydButton from '../../../../components/commonDesign/Button'
import FlexRow from '../../../../components/molecules/FlexRow'
import { useGetMeeting, useUpdateMeeting } from '../../../../api/meetings'
import { MEETING_TYPES } from './meetingConstants'

const meetingTypeOptions = MEETING_TYPES.map(t => ({ label: t.label, value: t.value }))
const PRE_JOIN_STATUSES = ['pending', 'scheduled']

function EditMeetingInner ({ meeting }) {
  const history = useHistory()
  const isPreJoin = PRE_JOIN_STATUSES.includes(meeting.status)
  const { mutateAsync: updateMeeting, isLoading: processing } = useUpdateMeeting()

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      meetingType: meeting.meetingType || '',
      title: meeting.title || '',
      meetingUrl: meeting.meetingUrl || '',
      botName: meeting.botName || '',
      joinAt: meeting.joinAt ? dayjs(meeting.joinAt).format('YYYY-MM-DDTHH:mm') : ''
    }
  })

  const rootError = form.formState.errors?.root?.message

  const onSubmit = useCallback(async (formData) => {
    const command = { meetingId: meeting.meetingId, title: formData.title || null, meetingType: formData.meetingType || null }

    if (isPreJoin) {
      command.meetingUrl = formData.meetingUrl
      command.botName = formData.botName
      if (formData.joinAt) command.joinAt = dayjs(formData.joinAt).toISOString()
    }

    try {
      form.clearErrors()
      await updateMeeting(command)
      history.push(`/denali-ai/meetings/${meeting.meetingId}`)
    } catch (err) {
      form.setError('root', { type: 'custom', message: err.message || 'Failed to update meeting.' })
    }
  }, [form, meeting, isPreJoin, updateMeeting, history])

  const handleSubmit = useCallback(async (e) => {
    form.clearErrors()
    await form.handleSubmit(onSubmit)(e)
  }, [form, onSubmit])

  return (
    <PageLayout variant='constrained'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div style={{ fontSize: 'var(--font-size-xs)', marginBottom: 2 }}>
            <Link to='/denali-ai/meetings' style={{ color: 'var(--dai-color-text-dim)', textDecoration: 'none' }}>Meetings</Link>
            <span style={{ color: 'var(--dai-color-text-dim)', margin: '0 4px' }}>/</span>
            <Link to={`/denali-ai/meetings/${meeting.meetingId}`} style={{ color: 'var(--dai-color-text-dim)', textDecoration: 'none' }}>{meeting.title || 'Meeting'}</Link>
          </div>
          <PageHeading title='Edit Meeting' />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name='meetingType'
            control={form.control}
            render={({ field }) => (
              <SydLabel label='Meeting Type'>
                <SydSelect disabled={processing} options={meetingTypeOptions} placeholder='Select a type...' {...field} />
              </SydLabel>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name='title'
            control={form.control}
            rules={{ maxLength: { value: 255, message: 'Maximum 255 characters' } }}
            render={({ field }) => (
              <SydLabel label='Title'>
                <SydInput width='inherit' disabled={processing} {...field} />
              </SydLabel>
            )}
          />
        </Grid>

        {isPreJoin && meeting.joinAt && (
          <Grid item xs={12}>
            <Controller
              name='joinAt'
              control={form.control}
              rules={{
                validate: v => {
                  if (!v) return true
                  if (dayjs(v).isBefore(dayjs().add(15, 'minute'))) return 'Must be at least 15 minutes from now'
                  return true
                }
              }}
              render={({ field, fieldState }) => (
                <SydLabel label='When' error={fieldState.error?.message}>
                  <SydInput type='datetime-local' width='inherit' disabled={processing} {...field} />
                </SydLabel>
              )}
            />
          </Grid>
        )}

        {isPreJoin && (
          <Grid item xs={12}>
            <Controller
              name='meetingUrl'
              control={form.control}
              rules={{ required: 'Meeting URL is required', pattern: { value: /^https?:\/\/.+/i, message: 'Enter a valid URL' } }}
              render={({ field, fieldState }) => (
                <SydLabel label='Meeting URL' required error={fieldState.error?.message}>
                  <SydInput width='inherit' disabled={processing} {...field} />
                </SydLabel>
              )}
            />
          </Grid>
        )}

        {isPreJoin && (
          <Grid item xs={12}>
            <Controller
              name='botName'
              control={form.control}
              render={({ field }) => (
                <SydLabel label='Notetaker Name'>
                  <SydInput width='inherit' disabled={processing} {...field} />
                </SydLabel>
              )}
            />
          </Grid>
        )}

        {rootError && (
          <Grid item xs={12}>
            <div className='font-size-sm p-md' style={{ backgroundColor: 'var(--color-danger-500)', color: 'var(--color-danger-050)', borderRadius: 'var(--border-radius-lg)' }}>
              {rootError}
            </div>
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <FlexRow className='gap-md justify-end'>
            <SydButton size='lg' variant='ghost' onClick={() => history.go(-1)} disabled={processing}>Cancel</SydButton>
            <SydButton size='lg' variant='primary' onClick={handleSubmit} processing={processing}>Save Changes</SydButton>
          </FlexRow>
        </Grid>
      </Grid>
    </PageLayout>
  )
}

EditMeetingInner.propTypes = {
  meeting: PropTypes.shape({
    meetingId: PropTypes.string.isRequired,
    title: PropTypes.string,
    meetingType: PropTypes.string,
    meetingUrl: PropTypes.string,
    botName: PropTypes.string,
    joinAt: PropTypes.string,
    status: PropTypes.string.isRequired
  }).isRequired
}

function EditMeeting () {
  const { meetingId } = useParams()
  const { data: meeting, isLoading } = useGetMeeting(meetingId, { poll: false })

  if (isLoading || !meeting) {
    return <PageLayout variant='constrained'><Loading /></PageLayout>
  }

  return <EditMeetingInner meeting={meeting} />
}

export default EditMeeting
